import { Token, computePoolAddress } from '@cryptoalgebra/kim-sdk';
import { useMemo } from 'react';
import { useAddress, useChainId, useContract } from '@thirdweb-dev/react';
import { getConfig } from '@app/config';
import { Address } from '@thirdweb-dev/sdk';
import { algebraPositionManagerABI } from '../../../abis/v3';
import { useAsync } from 'react-use';
import { useSupportedChain } from '@app/hooks/other/useSupportedChain';

export interface PositionFromTokenId {
  tokenId: number;
  feeGrowthInside0LastX128: bigint;
  feeGrowthInside1LastX128: bigint;
  liquidity: bigint;
  nonce: string;
  operator: string;
  tickLower: bigint;
  tickUpper: bigint;
  token0: Address;
  token1: Address;
  tokensOwed0: bigint;
  tokensOwed1: bigint;
  pool: Address;
}

function usePositionsFromTokenIds(tokenIds: any[] | undefined): {
  isLoading: boolean;
  positions: PositionFromTokenId[] | undefined;
} {
  const chainId = useSupportedChain()?.chainId;

  const inputs = useMemo(
    () => (tokenIds ? tokenIds.map(tokenId => tokenId.toString()) : []),
    [tokenIds]
  );

  const { contract } = useContract(
    getConfig(chainId)?.ALGEBRA_ADDRESSES.ALGEBRA_POSITION_MANAGER,
    algebraPositionManagerABI
  );

  const {
    value: results,
    loading: isLoading,
    error
  } = useAsync(async () => {
    if (!contract) {
      return [];
    }

    return await Promise.all(
      inputs.map(async x => {
        return await contract.call('positions', [Number(x)]);
      })
    );
  }, [contract, inputs]);

  const positions = useMemo(() => {
    if (!isLoading && tokenIds && !error && chainId) {
      return results
        ?.filter(v => !v.error)
        .map((call, i) => {
          const tokenId = tokenIds[i];
          const result = call as any;

          const pool = computePoolAddress({
            tokenA: new Token(chainId, result[2], 18),
            tokenB: new Token(chainId, result[3], 18)
          }) as Address;

          return {
            tokenId,
            feeGrowthInside0LastX128: result[7],
            feeGrowthInside1LastX128: result[8],
            liquidity: result[6],
            nonce: result[0],
            operator: result[1],
            tickLower: result[4],
            tickUpper: result[5],
            token0: result[2],
            token1: result[3],
            tokensOwed0: result[9],
            tokensOwed1: result[10],
            pool
          };
        });
    }

    return undefined;
  }, [isLoading, tokenIds, error, chainId, results]);

  return useMemo(() => {
    return {
      isLoading,
      positions
    };
  }, [isLoading, positions]);
}

export function usePositions() {
  const account = useAddress();
  const chainId = useSupportedChain()?.chainId;

  const { contract } = useContract(
    getConfig(chainId)?.ALGEBRA_ADDRESSES.ALGEBRA_POSITION_MANAGER,
    algebraPositionManagerABI
  );

  const { value: balanceResult, loading: balanceLoading } =
    useAsync(async () => {
      if (!contract || !account) {
        return [];
      }

      return await contract.call('balanceOf', [account]);
    }, [contract, account]);

  const tokenIdsArgs: [Address, number][] = useMemo(() => {
    if (!balanceResult || !account) return [];

    const tokenRequests: any[] = [];

    for (let i = 0; i < balanceResult; i++) {
      tokenRequests.push([account, i]);
    }

    return tokenRequests;
  }, [account, balanceResult]);

  const { value: tokenIds, loading: someTokenIdsLoading } =
    useAsync(async () => {
      if (!contract) {
        return [];
      }

      const res = await Promise.all(
        tokenIdsArgs.map(async args => {
          return await contract.call('tokenOfOwnerByIndex', args);
        })
      );

      return account ? res : [];
    }, [contract, account, tokenIdsArgs]);

  const { positions, isLoading: positionsLoading } =
    usePositionsFromTokenIds(tokenIds);

  return {
    loading: someTokenIdsLoading || balanceLoading || positionsLoading,
    positions
  };
}

export function usePosition(tokenId: string | number | undefined): {
  loading: boolean;
  position: PositionFromTokenId | undefined;
} {
  const tokenIdArr = useMemo(() => {
    if (!tokenId) return;

    return [tokenId];
  }, [tokenId]);

  const { isLoading, positions } = usePositionsFromTokenIds(tokenIdArr);

  return useMemo(() => {
    return {
      loading: isLoading,
      position: positions?.[0]
    };
  }, [isLoading, positions]);
}
